// =============================================================================
// PEDRA — src/optimizer/rng.ts
// Gerador pseudoaleatorio DETERMINISTICO.
//
// Regra de ouro do projeto: `Math.random()` e `Date.now()` sao PROIBIDOS dentro
// de `src/core` e `src/optimizer`. Toda aleatoriedade nasce de `Settings.seed`.
// Consequencia: a mesma entrada, com a mesma semente, produz sempre o mesmo
// plano de corte — em qualquer maquina, em qualquer ordem de execucao.
// =============================================================================

/** Funcao geradora: devolve um float uniforme em [0, 1). */
export type Rng = () => number;

/**
 * Mulberry32 — PRNG de 32 bits, periodo 2^32, estado de uma unica palavra.
 * Rapido, simples e com qualidade estatistica suficiente para embaralhar
 * ordens de pecas. Cada chamada avanca o estado.
 */
export function mulberry32(seed: number): Rng {
  // Normaliza qualquer numero (inclusive NaN/negativo/fracionario) para uint32.
  let a = (Number.isFinite(seed) ? Math.trunc(seed) : 0) >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Xorshift32 — alternativa com estado diferente, usada quando queremos duas
 * sequencias independentes a partir da mesma semente base.
 * Nunca devolve 0 como estado (o gerador travaria).
 */
export function xorshift32(seed: number): Rng {
  let x = (Number.isFinite(seed) ? Math.trunc(seed) : 0) >>> 0;
  if (x === 0) x = 0x9e3779b9;
  return function next(): number {
    x ^= x << 13;
    x >>>= 0;
    x ^= x >>> 17;
    x ^= x << 5;
    x >>>= 0;
    return x / 4294967296;
  };
}

/**
 * FNV-1a de 32 bits sobre a concatenacao das partes.
 * Serve para derivar sementes filhas estaveis:
 * `mulberry32(hash32(seed, material, comboIndex, restart))`.
 * O resultado NAO depende da ordem em que as tentativas rodam.
 */
export function hash32(...parts: Array<number | string>): number {
  let h = 0x811c9dc5;
  for (let p = 0; p < parts.length; p++) {
    const raw = parts[p];
    const s = typeof raw === 'number' ? numberToKey(raw) : raw;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    // Separador entre partes: evita que ('ab','c') colida com ('a','bc').
    h ^= 0x1f;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Representacao textual estavel de um numero para o hash. */
function numberToKey(n: number): string {
  if (!Number.isFinite(n)) return 'nan';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(6);
}

/** Inteiro uniforme em [0, n). Devolve 0 quando `n <= 1`. */
export function randInt(rng: Rng, n: number): number {
  if (!Number.isFinite(n) || n <= 1) return 0;
  const v = Math.floor(rng() * n);
  return v >= n ? n - 1 : v < 0 ? 0 : v;
}

/** Embaralhamento Fisher-Yates in-place, de tras para frente. */
export function shuffle<T>(arr: T[], rng: Rng): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = randInt(rng, i + 1);
    const tmp = arr[i] as T;
    arr[i] = arr[j] as T;
    arr[j] = tmp;
  }
}

/**
 * Escolhe um elemento com probabilidade proporcional ao peso.
 * Pesos negativos ou nao finitos contam como zero. Se a soma for zero,
 * devolve o primeiro elemento (comportamento deterministico).
 */
export function pickWeighted<T>(items: readonly T[], weights: readonly number[], rng: Rng): T {
  const n = Math.min(items.length, weights.length);
  if (n === 0) throw new RangeError('pickWeighted: lista vazia');
  let total = 0;
  for (let i = 0; i < n; i++) {
    const w = weights[i] as number;
    if (Number.isFinite(w) && w > 0) total += w;
  }
  if (total <= 0) return items[0] as T;
  let r = rng() * total;
  for (let i = 0; i < n; i++) {
    const w = weights[i] as number;
    if (!Number.isFinite(w) || w <= 0) continue;
    r -= w;
    if (r <= 0) return items[i] as T;
  }
  return items[n - 1] as T;
}
